import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SectionHeader from '../ui/SectionHeader';
import { servicesList } from '../../data/services';

const Section = styled.section`
  padding: 96px 0;
  background: #fff;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.div)`
  padding: 28px 24px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid #e8eef3;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.04);
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 16px 40px rgba(14, 107, 60, 0.1);
    border-color: rgba(14, 107, 60, 0.2);
  }
`;

const IconBox = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  border: 1.5px solid rgba(14, 107, 60, 0.2);
  background: rgba(14, 107, 60, 0.04);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;

  i {
    font-size: 1.15rem;
    color: #0e6b3c;
  }
`;

const Title = styled.h3`
  font-family: var(--font-display);
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 8px;
`;

const Desc = styled.p`
  font-size: 0.86rem;
  color: #64748b;
  line-height: 1.6;
`;

const ExploreBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 40px;
  padding: 13px 28px;
  border-radius: 100px;
  background: #0e6b3c;
  color: #fff;
  font-weight: 700;
  font-size: 0.9rem;
  font-family: var(--font-display);
  text-decoration: none;
  box-shadow: 0 8px 24px rgba(14, 107, 60, 0.25);

  &:hover {
    background: #0a5a32;
  }
`;

const ServicesPreview = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.08 });

  return (
    <Section ref={ref} id="services">
      <div className="container">
        <SectionHeader label="What We Do" title="Our Services" />
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
        >
          <Grid>
            {servicesList.map((s) => (
              <Card
                key={s.title}
                as={s.link ? Link : undefined}
                to={s.link || undefined}
                style={s.link ? { textDecoration: 'none', color: 'inherit', cursor: 'pointer' } : undefined}
                variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
              >
                <IconBox><i className={s.icon} /></IconBox>
                <Title>{s.title}</Title>
                <Desc>{s.description}</Desc>
                {s.link && (
                  <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0e6b3c', marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    Learn more <i className="fas fa-arrow-right" style={{ fontSize: '0.7rem' }} />
                  </span>
                )}
              </Card>
            ))}
          </Grid>
        </motion.div>
        <div style={{ textAlign: 'center' }}>
          <ExploreBtn to="/services">Explore All Services <i className="fas fa-arrow-right" /></ExploreBtn>
        </div>
      </div>
    </Section>
  );
};

export default ServicesPreview;
